import { computed, DestroyRef, effect, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment-jalaali';

import { UserCompanyTypeEnum, UserCompanyTypeLabel } from '@app/core/types/enums/user-company-type.enum';
import { AppCityService, AppProvinceService } from '@app/core/services';
import { SERVER_JALALI_DATE_FORMAT } from '@app/core/constants/datetime.constant';
import { parseFieldNumber } from '@app/core/utils/field-value.util';
import { toEnglishDigits } from '@app/core/utils/digit.util';
import { REGISTRATION_STEP_DEFINITIONS } from '@app/features/registration/constants/registration-steps.const';
import {
  RegistrationFieldId,
  RegistrationPreviewSection,
} from '@app/features/registration/types';

import { RegistrationStore } from './registration-store.service';
import { RegistrationWizardService } from './registration-wizard.service';

const EMPTY_VALUE = '—';

@Injectable({ providedIn: 'root' })
export class RegistrationPreviewPresenter {
  private readonly store = inject(RegistrationStore);
  private readonly wizard = inject(RegistrationWizardService);
  private readonly provinceService = inject(AppProvinceService);
  private readonly cityService = inject(AppCityService);
  private readonly destroyRef = inject(DestroyRef);

  readonly previewSections = computed((): RegistrationPreviewSection[] => {
    this.store.registration();
    this.cityService.citiesByProvinceMap();

    const sections: RegistrationPreviewSection[] = [
      this.buildBasicInfoSection(),
      this.buildPersonalInfoSection(),
    ];

    if (this.wizard.isLegalEntity()) {
      sections.push(this.buildLegalInfoSection());
    }

    sections.push(this.buildAddressSection());

    return sections;
  });

  constructor() {
    this.provinceService.ensureLoaded().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    effect(onCleanup => {
      this.store.registration();

      const provinceId = parseFieldNumber(this.store.getFieldValue(RegistrationFieldId.ProvinceId));

      if (provinceId == null) {
        return;
      }

      const subscription = this.cityService.ensureLoaded(provinceId).subscribe();

      onCleanup(() => subscription.unsubscribe());
    });
  }

  private buildBasicInfoSection(): RegistrationPreviewSection {
    return {
      key: 'basicInfo',
      title: REGISTRATION_STEP_DEFINITIONS['basic-information'].title,
      items: [
        { label: 'نوع حساب', value: this.formatIsLegal() },
        { label: 'آدرس ایمیل', value: this.fieldValue(RegistrationFieldId.Email) },
      ],
    };
  }

  private buildPersonalInfoSection(): RegistrationPreviewSection {
    return {
      key: 'personalInfo',
      title: REGISTRATION_STEP_DEFINITIONS['personal-information'].title,
      items: [
        { label: 'کد ملی', value: this.fieldValue(RegistrationFieldId.NationalId) },
        { label: 'تاریخ تولد', value: this.formatBirthDate() },
      ],
    };
  }

  private buildLegalInfoSection(): RegistrationPreviewSection {
    return {
      key: 'legalInfo',
      title: REGISTRATION_STEP_DEFINITIONS['legal-information'].title,
      items: [
        { label: 'نام شرکت', value: this.fieldValue(RegistrationFieldId.CompanyName) },
        { label: 'نام برند (اختیاری)', value: this.fieldValue(RegistrationFieldId.BusinessName) },
        { label: 'شناسه ملی', value: this.fieldValue(RegistrationFieldId.CompanyNationalId) },
        { label: 'شماره ثبت', value: this.fieldValue(RegistrationFieldId.RegistrationNumber) },
        { label: 'نوع شرکت', value: this.formatCompanyType() },
      ],
    };
  }

  private buildAddressSection(): RegistrationPreviewSection {
    return {
      key: 'addressContact',
      title: REGISTRATION_STEP_DEFINITIONS['address-information'].title,
      items: [
        { label: 'کد پستی', value: this.fieldValue(RegistrationFieldId.PostalCode) },
        { label: 'آدرس', value: this.formatAddress() },
        { label: 'شماره تلفن ثابت', value: this.fieldValue(RegistrationFieldId.PhoneNumber) },
      ],
    };
  }

  private fieldValue(fieldId: RegistrationFieldId): string {
    return this.formatDisplayValue(this.store.getFieldValue(fieldId));
  }

  private formatDisplayValue(value: string | null): string {
    if (value == null || value.trim() === '') {
      return EMPTY_VALUE;
    }

    return value;
  }

  private formatIsLegal(): string {
    return this.wizard.isLegalEntity() ? 'حقوقی' : 'حقیقی';
  }

  private formatBirthDate(): string {
    const value = this.store.getFieldValue(RegistrationFieldId.BirthDate);

    if (value == null || value.trim() === '') {
      return EMPTY_VALUE;
    }

    const digits = toEnglishDigits(value).replace(/\D/g, '');
    const parsed = moment(digits, SERVER_JALALI_DATE_FORMAT, true);

    return parsed.isValid() ? parsed.format('jYYYY/jMM/jDD') : value;
  }

  private formatCompanyType(): string {
    const value = this.store.getFieldValue(RegistrationFieldId.CompanyType);

    if (value == null || value.trim() === '') {
      return EMPTY_VALUE;
    }

    const companyType = Number(value) as UserCompanyTypeEnum;

    return UserCompanyTypeLabel[companyType] ?? value;
  }

  private formatAddress(): string {
    const parts = [
      this.resolveProvinceName(),
      this.resolveCityName(),
      this.store.getFieldValue(RegistrationFieldId.Avenue),
      this.store.getFieldValue(RegistrationFieldId.Alley),
      this.store.getFieldValue(RegistrationFieldId.Plaque),
    ].filter((part): part is string => part != null && part.trim() !== '');

    return parts.length ? parts.join('، ') : EMPTY_VALUE;
  }

  private resolveProvinceName(): string | null {
    const provinceId = parseFieldNumber(this.store.getFieldValue(RegistrationFieldId.ProvinceId));

    if (provinceId == null) {
      return null;
    }

    return this.provinceService.provinces()?.find(item => item.value === provinceId)?.name ?? String(provinceId);
  }

  private resolveCityName(): string | null {
    const provinceId = parseFieldNumber(this.store.getFieldValue(RegistrationFieldId.ProvinceId));
    const cityId = parseFieldNumber(this.store.getFieldValue(RegistrationFieldId.CityId));

    if (provinceId == null || cityId == null) {
      return null;
    }

    return this.cityService.getCityName(provinceId, cityId) ?? String(cityId);
  }
}
